// Journey choreography math — single source of truth for both the R3F camera
// rig and the DOM tile transforms. Eight beats along a world line (spacing
// BEAT_SPACING on -Z); ~70% of each beat's scroll span dwells at the set
// piece, ~30% warps to the next. All outputs are TARGETS — consumers damp
// them with delta time (maath), never fixed-alpha lerp.
export const BEAT_COUNT = 8;
export const BEAT_SPACING = 26;
export const DWELL = 0.7;

export const stationZ = (i) => -BEAT_SPACING * i;

export function journeyPose(p) {
  const clamped = Math.min(0.99999, Math.max(0, p));
  const span = 1 / BEAT_COUNT;
  const beat = Math.min(BEAT_COUNT - 1, Math.floor(clamped / span));
  const local = (clamped - beat * span) / span;
  if (local < DWELL || beat === BEAT_COUNT - 1) {
    return { beat, phase: 'dwell', t: Math.min(1, local / DWELL), warp: 0 };
  }
  const wt = (local - DWELL) / (1 - DWELL);
  return { beat, phase: 'warp', t: wt, warp: Math.sin(Math.PI * wt) };
}

const smooth = (v) => {
  const x = Math.min(1, Math.max(0, v));
  return x * x * (3 - 2 * x);
};
const easeInOut = (v) => (v < 0.5 ? 2 * v * v : 1 - Math.pow(-2 * v + 2, 2) / 2);
const lerp = (a, b, v) => a + (b - a) * v;
const lerp3 = (a, b, v) => [lerp(a[0], b[0], v), lerp(a[1], b[1], v), lerp(a[2], b[2], v)];

// Set-piece world positions (see JourneyScene for what sits at each)
export const PIECES = {
  swirl: [0, 0.15, stationZ(0) - 7],
  aboutPlanet: [2.3, -0.15, stationZ(1) - 6],
  orbitPlanet: [-1.9, 0, stationZ(2) - 6.5],
  nebula: [0, 0, stationZ(3) - 9],
  star: [1.6, 0.35, stationZ(4) - 6],
  ringPlanet: [0, -0.5, stationZ(5) - 7],
  dustText: [0, 0.9, stationZ(6) - 7 + 3.1], // standing in the ring, radial to ringPlanet
  system: [0, -0.3, stationZ(7) - 8],
};

export const RING_STOPS = 9; // 4 certs · 3 skill clusters · education · tools
export const RING_RADIUS = 2.7;
export const RING_CAM_RADIUS = 6.15; // zoomed out — tablet + planet + ring all in frame

// Stepped orbit angle for the ring flyover: quick eased hop (35% of a stop's
// span) then a legible plateau (65%) at each tablet.
export function ringAngle(t) {
  const total = RING_STOPS - 1;
  const x = t * total;
  const i = Math.min(total - 1, Math.floor(x));
  const f = x - i;
  const hop = f < 0.35 ? easeInOut(f / 0.35) : 1;
  return ((i + hop) / total) * Math.PI * 1.5 - Math.PI * 0.25; // 270° sweep
}

export function tabletAngle(index) {
  return (index / (RING_STOPS - 1)) * Math.PI * 1.5 - Math.PI * 0.25;
}

// Per-beat dwell rigs: t ∈ [0,1] → camera {pos, look, fov}
const RIGS = [
  // 0 · hero swirl — slow push toward the galaxy
  (t) => ({
    pos: [0, 0.1, lerp(4, 0.5, smooth(t))],
    look: PIECES.swirl,
    fov: lerp(48, 44, t),
  }),
  // 1 · about — drift alongside the planet while the tile approaches
  (t) => ({
    pos: [lerp(-0.8, 0.4, smooth(t)), 0.1, stationZ(1) + lerp(3.5, 0.5, smooth(t))],
    look: PIECES.aboutPlanet,
    fov: 44,
  }),
  // 2 · case 1 — sweep 130° around the planet, moon alongside
  (t) => {
    const angle = lerp(0.45, 0.45 + Math.PI * 0.72, smooth(t));
    const [px, py, pz] = PIECES.orbitPlanet;
    return {
      pos: [px + Math.cos(angle) * 4.4, py + 0.45 + t * 0.2, pz + Math.sin(angle) * 4.4],
      look: PIECES.orbitPlanet,
      fov: 42,
    };
  },
  // 3 · case 2 — fly THROUGH the gap between the meeting tiles into the nebula
  (t) => ({
    pos: [0, 0.05, stationZ(3) + lerp(4.5, -4, smooth(t))],
    look: [0, 0, stationZ(3) - 16],
    fov: lerp(44, 50, t),
  }),
  // 4 · experience — close pass on the star
  (t) => {
    const [sx, sy, sz] = PIECES.star;
    return {
      pos: [lerp(-1.4, -0.2, smooth(t)), sy - 0.15, sz + lerp(4.4, 2.6, smooth(t))],
      look: PIECES.star,
      fov: 42,
    };
  },
  // 5 · credentials — flying over the ring, stepped stops facing each tablet
  (t) => {
    const angle = ringAngle(t);
    const [px, py, pz] = PIECES.ringPlanet;
    const camA = angle + 0.13; // slight trail for parallax, near face-on read
    return {
      pos: [px + Math.cos(camA) * RING_CAM_RADIUS, py + 1.8, pz + Math.sin(camA) * RING_CAM_RADIUS],
      look: [px + Math.cos(angle) * RING_RADIUS, py + 0.42, pz + Math.sin(angle) * RING_RADIUS],
      fov: 41,
    };
  },
  // 6 · proof — settle on the dust-text standing in the ring
  (t) => {
    const [dx, dy, dz] = PIECES.dustText;
    return {
      pos: [dx + lerp(1.6, 0, smooth(t)), dy + 0.25, dz + lerp(4.6, 3.4, smooth(t))],
      look: [dx, dy, dz],
      fov: lerp(42, 38, smooth(t)),
    };
  },
  // 7 · contact — pull up and back until the whole system sits in frame
  (t) => {
    const [sx, sy, sz] = PIECES.system;
    return {
      pos: [sx, sy + lerp(0.6, 2.6, smooth(t)), sz + lerp(4, 9.5, smooth(t))],
      look: [sx, sy, sz],
      fov: lerp(44, 52, smooth(t)),
    };
  },
];

export function computeJourneyPose(progress) {
  const { beat, phase, t, warp } = journeyPose(progress);
  if (phase === 'dwell') {
    const rig = RIGS[beat](t);
    return { ...rig, beat, phase, t, warp: 0 };
  }
  const from = RIGS[beat](1);
  const to = RIGS[Math.min(BEAT_COUNT - 1, beat + 1)](0);
  const e = easeInOut(t);
  return {
    pos: lerp3(from.pos, to.pos, e),
    look: lerp3(from.look, to.look, e),
    fov: lerp(from.fov, to.fov, e) + warp * 9,
    beat,
    phase,
    t,
    warp,
  };
}
